import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { API } from "@/lib/api";

const TOKEN_KEY = "lb_admin_token";

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const LoginCard = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, {
        username,
        password,
      });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      toast.success(`Welcome back, ${data.username}.`);
      onLogin(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0A0A0A] p-10"
      >
        <div className="text-xs uppercase tracking-[0.35em] text-amber-500 mb-3">
          Admin Access
        </div>
        <h1 className="font-serif-display text-3xl text-white mb-8">
          Welcome back, <em className="italic text-amber-400">Leo.</em>
        </h1>

        <Label htmlFor="u" className="text-zinc-300">
          Username
        </Label>
        <Input
          id="u"
          required
          autoFocus
          data-testid="admin-username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="mt-2 mb-5 bg-black/40 border-white/15 text-white h-12 rounded-xl"
        />

        <Label htmlFor="p" className="text-zinc-300">
          Password
        </Label>
        <Input
          id="p"
          type="password"
          required
          data-testid="admin-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-2 mb-8 bg-black/40 border-white/15 text-white h-12 rounded-xl"
        />

        <Button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="w-full bg-amber-500 hover:bg-amber-400 text-black font-medium rounded-full py-6"
        >
          {loading ? "Signing in..." : "Sign in →"}
        </Button>
      </form>
    </div>
  );
};

const Empty = ({ label }) => (
  <div className="text-center py-16 text-zinc-500 text-sm">
    No {label} yet. They'll show up here the moment they come in.
  </div>
);

const BookingRow = ({ b }) => (
  <div
    data-testid={`booking-row-${b.id}`}
    className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 hover:border-amber-600/30 transition-colors"
  >
    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
      <div>
        <div className="font-serif-display text-2xl text-white">{b.name}</div>
        <div className="text-xs text-zinc-500 mt-1">
          Submitted {fmtDate(b.created_at)}
        </div>
      </div>
      <span className="text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30">
        {b.program || "—"}
      </span>
    </div>

    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
      <div>
        <span className="text-zinc-500">Email:</span>{" "}
        <a
          href={`mailto:${b.email}`}
          className="text-amber-400 hover:underline"
        >
          {b.email}
        </a>
      </div>
      <div>
        <span className="text-zinc-500">Phone:</span>{" "}
        <span className="text-zinc-200">{b.phone || "—"}</span>
      </div>
      <div>
        <span className="text-zinc-500">Date:</span>{" "}
        <span className="text-zinc-200">{b.preferred_date}</span>
      </div>
      <div>
        <span className="text-zinc-500">Time:</span>{" "}
        <span className="text-zinc-200">
          {b.preferred_time} {b.timezone ? `(${b.timezone})` : ""}
        </span>
      </div>
    </div>

    {b.notes && (
      <div className="mt-4 p-4 bg-black/40 border-l-2 border-amber-500/40 rounded-r text-zinc-300 text-sm whitespace-pre-wrap">
        {b.notes}
      </div>
    )}
  </div>
);

const ContactRow = ({ c }) => (
  <div
    data-testid={`contact-row-${c.id}`}
    className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 hover:border-amber-600/30 transition-colors"
  >
    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
      <div>
        <div className="font-serif-display text-2xl text-white">{c.name}</div>
        <div className="text-xs text-zinc-500 mt-1">{fmtDate(c.created_at)}</div>
      </div>
      <a
        href={`mailto:${c.email}`}
        className="text-amber-400 text-sm hover:underline"
      >
        {c.email}
      </a>
    </div>
    {c.phone && (
      <div className="text-sm text-zinc-400 mb-3">Phone: {c.phone}</div>
    )}
    <div className="p-4 bg-black/40 border-l-2 border-amber-500/40 rounded-r text-zinc-300 text-sm whitespace-pre-wrap">
      {c.message}
    </div>
  </div>
);

const Dashboard = ({ onLogout }) => {
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);

  const token = localStorage.getItem(TOKEN_KEY);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([
        axios.get(`${API}/admin/bookings`, authHeaders),
        axios.get(`${API}/admin/contacts`, authHeaders),
      ]);
      setBookings(b.data);
      setContacts(c.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Sign in again.");
        onLogout();
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/email-test`,
        {},
        authHeaders
      );
      if (data.sent) {
        toast.success("Test email sent. Check your inbox.");
      } else {
        toast.error("Resend isn't configured properly.");
      }
    } catch {
      toast.error("Could not send test email.");
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span className="font-serif-display text-xl text-white tracking-wider">
              LEO <span className="text-amber-500">BIANCHI</span>
              <span className="ml-3 text-xs uppercase tracking-[0.3em] text-zinc-500">
                Admin
              </span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              data-testid="admin-test-email"
              onClick={sendTestEmail}
              disabled={sendingTest}
              className="border-white/20 bg-white/5 hover:bg-white/10 text-white rounded-full text-xs"
            >
              {sendingTest ? "Sending..." : "Send test email"}
            </Button>
            <Button
              variant="outline"
              data-testid="admin-refresh"
              onClick={fetchAll}
              className="border-white/20 bg-white/5 hover:bg-white/10 text-white rounded-full text-xs"
            >
              Refresh
            </Button>
            <Button
              data-testid="admin-logout"
              onClick={onLogout}
              className="bg-amber-500 hover:bg-amber-400 text-black rounded-full text-xs"
            >
              Log out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.35em] text-amber-500 mb-3">
            — Inbox
          </p>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-white">
            Your <em className="italic text-amber-400">leads.</em>
          </h1>
          <p className="text-zinc-400 mt-3">
            Everything submitted from your site, in one place. You also get an
            email for each new submission.
          </p>
        </div>

        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="bg-[#0A0A0A] border border-white/10 rounded-full p-1">
            <TabsTrigger
              value="bookings"
              data-testid="tab-bookings"
              className="rounded-full data-[state=active]:bg-amber-500 data-[state=active]:text-black px-6"
            >
              Bookings ({bookings.length})
            </TabsTrigger>
            <TabsTrigger
              value="contacts"
              data-testid="tab-contacts"
              className="rounded-full data-[state=active]:bg-amber-500 data-[state=active]:text-black px-6"
            >
              Messages ({contacts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-8 space-y-4">
            {loading ? (
              <div className="text-zinc-500 text-sm py-8">Loading...</div>
            ) : bookings.length === 0 ? (
              <Empty label="bookings" />
            ) : (
              bookings.map((b) => <BookingRow key={b.id} b={b} />)
            )}
          </TabsContent>

          <TabsContent value="contacts" className="mt-8 space-y-4">
            {loading ? (
              <div className="text-zinc-500 text-sm py-8">Loading...</div>
            ) : contacts.length === 0 ? (
              <Empty label="messages" />
            ) : (
              contacts.map((c) => <ContactRow key={c.id} c={c} />)
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default function Admin() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      setAuthed(false);
      return;
    }
    axios
      .get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${t}` } })
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      });
  }, []);

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    navigate("/admin", { replace: true });
  };

  if (authed === null) {
    return (
      <div className="min-h-screen bg-[#050505] text-zinc-500 flex items-center justify-center">
        Loading...
      </div>
    );
  }

  return authed ? (
    <Dashboard onLogout={handleLogout} />
  ) : (
    <LoginCard onLogin={() => setAuthed(true)} />
  );
}
